import { createSelector } from 'reselect';
import type { Board } from '@atlassian/jira-navigation-types/src/types.tsx';
import { SEARCH_MAX_RESULTS } from '../model/constants.tsx';
import {
	getRecentBoards as getUnfilteredRecentBoards,
	isSearchUsed,
	getSearchResults,
	getSelectedBoard as stateGetSelectedBoard,
	getBoardsInLocation as stateGetBoardsInLocation,
	getRecentlyClickedBoard,
	getInitialBoardsProps as getInitialBoards,
} from '../state/selectors.tsx';
import type { State } from '../state/types.tsx';

export const getRecentBoards = createSelector(
	getUnfilteredRecentBoards,
	stateGetBoardsInLocation,
	(recentBoards, boardsInLocation) => {
		const isBoardIncluded = ({ id }: Board, boards: Board[]) =>
			boards.findIndex((board) => board.id === id) !== -1;

		return recentBoards.filter((board) => !isBoardIncluded(board, boardsInLocation));
	},
);

export const getSelectedBoard = (state: State) =>
	stateGetSelectedBoard(state) || getRecentlyClickedBoard(state);

export const getOtherBoards = (state: State) => {
	if (isSearchUsed(state)) {
		return getSearchResults(state).notInLocation.values;
	}

	return getRecentBoards(state);
};

export const getBoardsInLocation = (state: State) => {
	if (isSearchUsed(state)) {
		return getSearchResults(state).inLocation.values;
	}

	return stateGetBoardsInLocation(state);
};

export const getRemainingShowMoreBoards = (state: State) => {
	const { values, total } = isSearchUsed(state)
		? getSearchResults(state).inLocation
		: getInitialBoards(state).inLocation;

	return Math.min(total - values.length, SEARCH_MAX_RESULTS);
};

export const hasShowMoreButton = (state: State) => getRemainingShowMoreBoards(state) > 0;
